import { useQuery } from '@tanstack/react-query';

import { setActiveCart } from 'src/store/actions/MobileCheckoutActions';
import { useAppDispatch, useAppSelector } from 'src/utils';
import { post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

export const PollPayment = 'poll-payment';

const POLL_PAYMENT_URL = '/payment/poll-payment';

type usePollPaymentType = {
  ElectronicPaymentType: string;
  ShipmentId: number;
};

// For a Hub-initiated DutchiePay (Pay By Bank) decline, LeafLogix's Poll()
// (PaymentsHubPaymentProcessor.cs) now also returns the structured decline fields
// (BudtenderMessage and ErrorCode) stored in dbo.PaymentsHubDutchiePayDecline.
// These are only populated for DutchiePay Hub declines - other Hub payment methods (Datacap,
// GeneriPay) continue to only set PollingError.
export type ElectronicPaymentResponse = {
  BudtenderMessage?: string;
  CreditPaid: number;
  DebitPaid: number;
  ErrorCode?: string;
  PaymentSuccess: boolean;
  PollingComplete: boolean;
  PollingError: string;
  TipAmount?: number;
};

// Thrown by queryFn when the terminal/processor hasn't produced a final result yet - a typed
// signal to keep polling, distinct from a `post()` rejection (network/4xx/5xx). Both retry
// identically (see `retry` below); the distinction is only so `handlePollPayment` can tell
// "gave up after ~2 minutes with no final status" apart from "the last attempt errored".
export class PollNotCompleteError extends Error {
  constructor() {
    super('Poll payment: not complete yet');
    this.name = 'PollNotCompleteError';
  }
}

export function usePollPaymentQuery({ ElectronicPaymentType, ShipmentId }: usePollPaymentType) {
  const dispatch = useAppDispatch();
  const baseAPIParams = useBaseAPIParams();
  const activeCart = useAppSelector((state) => state.mobileCheckout.activeCart);

  // TError is `unknown`, not the default `Error`: `post()` rejects with plain strings, so
  // `result.error` is really `string | Error | PollNotCompleteError`.
  return useQuery<ElectronicPaymentResponse, unknown>({
    queryKey: [PollPayment, ShipmentId],
    queryFn: async () => {
      const resp = await post<ElectronicPaymentResponse>(POLL_PAYMENT_URL, {
        ...baseAPIParams,
        ShipmentId,
        ElectronicPaymentType,
      });

      if (resp.PollingComplete) {
        if (resp.PaymentSuccess) {
          dispatch(setActiveCart({ ...activeCart, TipAmount: resp.TipAmount }));
          return resp;
        }
        // When PaymentSuccess is false, return the response so the component can access PollingError
        return resp;
      }

      // Not done yet - signal a retry.
      throw new PollNotCompleteError();
    },
    enabled: false,
    retryDelay: 5000,
    // Retry every failure identically, including a `post()` rejection: a single transient error
    // must not end polling and report failure while the terminal may still approve the payment.
    retry: 24,
    refetchInterval: false,
  });
}
